import { useEffect, useState } from "react";
import { ApiError, type Catalogue, type Order, type PaymentResult, type Quote, type Service, api, downloadInvoice } from "../lib/api";
import { date, planLength, rupees } from "../lib/format";
import { Notice, SandboxPanel } from "../components/Shell";
import { TelegramJoin } from "../components/TelegramJoin";

interface RazorpayResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}
declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => { open(): void };
  }
}

function loadCheckout(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Could not load Razorpay"));
    document.head.appendChild(s);
  });
}

export function PlanStep({ onPaid, preselect = null, email = null }: { onPaid: () => void; preselect?: string | null; email?: string | null }) {
  const [services, setServices] = useState<Service[]>([]);
  const [serviceId, setServiceId] = useState<string | null>(preselect);
  const [planId, setPlanId] = useState<string | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [consent, setConsent] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<PaymentResult | null>(null);
  const [sandbox, setSandbox] = useState(false);

  useEffect(() => {
    api<Catalogue>("/v1/catalogue").then((c) => setServices(c.services), () => setError("Could not load plans."));
    api<{ sandbox: boolean }>("/v1/sandbox/status").then((s) => setSandbox(s.sandbox), () => setSandbox(false));
  }, []);

  useEffect(() => {
    setQuote(null);
    setError(null);
    if (!planId) return;
    api<Quote>("/v1/onboarding/quote", { body: { planId, advanceFeeConsent: true } }).then(setQuote, (e: unknown) =>
      setError(e instanceof ApiError ? e.message : "Could not price this plan."),
    );
  }, [planId]);

  const service = services.find((s) => s.id === serviceId);

  async function verify(r: RazorpayResponse) {
    const v = await api<PaymentResult>("/v1/onboarding/orders/verify", {
      body: { orderId: r.razorpay_order_id, paymentId: r.razorpay_payment_id, signature: r.razorpay_signature },
    });
    setDone(v);
    setOrder(null);
  }

  async function pay() {
    setBusy(true);
    setError(null);
    setReasons([]);
    try {
      const o = await api<Order>("/v1/onboarding/orders", { body: { planId, advanceFeeConsent: consent } });
      if (o.sandbox) {
        setOrder(o);
        return;
      }
      await loadCheckout();
      new window.Razorpay!({
        key: o.keyId,
        amount: o.amountPaise,
        currency: o.currency,
        order_id: o.orderId,
        name: "Finversify",
        description: o.description,
        prefill: o.prefill,
        theme: { color: "#011445" },
        handler: (r: RazorpayResponse) => void verify(r).catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Payment check failed.")),
      }).open();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setReasons(err.reasons);
      } else setError("Could not start the payment.");
    } finally {
      setBusy(false);
    }
  }

  async function sandboxPay() {
    setBusy(true);
    try {
      const r = await api<{ paymentId: string; signature: string }>(`/v1/sandbox/payments/${order!.orderId}/pay`, { method: "POST" });
      await verify({ razorpay_order_id: order!.orderId, razorpay_payment_id: r.paymentId, razorpay_signature: r.signature });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Test payment failed.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    const sub = done.subscription;
    return done.activated ? (
      <div>
        <h1>{sub.isRenewal ? "Renewal confirmed" : "You're subscribed"}</h1>
        <Notice tone="good">
          Payment received for {sub.service}, {date(sub.periodStart)} to {date(sub.periodEnd)}.{" "}
          {sub.isRenewal ? "Your new period starts straight after the current one." : "New calls appear on your dashboard the moment they are published."}{" "}
          We have emailed your tax invoice {done.invoiceNumber}
          {email ? ` to ${email}` : ""}.
        </Notice>
        <div className="paid-actions">
          <TelegramJoin subscriptionId={sub.id} access={sub.telegram} sandbox={sandbox} onChange={(t) => setDone({ ...done, subscription: { ...sub, telegram: t } })} />
          <button className="link" onClick={() => void downloadInvoice(done.paymentId, done.invoiceNumber).catch((e: Error) => setError(e.message))}>
            Download invoice {done.invoiceNumber}
          </button>
        </div>
        {error && <Notice tone="bad">{error}</Notice>}
        <button className="btn" onClick={onPaid}>
          Go to my calls
        </button>
      </div>
    ) : (
      <div>
        <h1>Payment received, subscription on hold</h1>
        <Notice tone="warn">
          We could not start this subscription, so your payment will be refunded in full within 5–7 working days.
          <ul>{done.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
        </Notice>
      </div>
    );
  }

  return (
    <div>
      <h1>Choose your service</h1>
      {error && (
        <Notice tone="bad">
          {error}
          {reasons.length > 1 && <ul>{reasons.map((r) => <li key={r}>{r}</li>)}</ul>}
        </Notice>
      )}
      <div className="services" role="group" aria-label="Services">
        {services.map((s) => (
          <button
            key={s.id}
            className="service"
            aria-pressed={s.id === serviceId}
            onClick={() => {
              setServiceId(s.id);
              setPlanId(null);
              setOrder(null);
            }}
          >
            <h3>{s.name}</h3>
            <span className="small muted">{s.summary}</span>
          </button>
        ))}
      </div>

      {service && (
        <>
          <h2>How long?</h2>
          <div className="durations" role="group" aria-label="Plan length">
            {service.plans.map((p) => (
              <button key={p.id} className="duration" aria-pressed={p.id === planId} onClick={() => setPlanId(p.id)}>
                {planLength(p.days)}: {rupees(p.feePaise)}
              </button>
            ))}
          </div>
          <p className="small muted">Prices exclude 18% GST.</p>
        </>
      )}

      {quote && (
        <>
          <table className="bill">
            <tbody>
              <tr>
                <td>
                  {service?.name}
                  {quote.isRenewal ? " (renewal)" : ""}, {date(quote.periodStart)} to {date(quote.periodEnd)}
                </td>
                <td className="num">{rupees(quote.feePaise)}</td>
              </tr>
              <tr>
                <td>GST at 18%</td>
                <td className="num">{rupees(quote.gstPaise)}</td>
              </tr>
              <tr className="total">
                <td>Total</td>
                <td className="num">{rupees(quote.totalPaise)}</td>
              </tr>
            </tbody>
          </table>
          {quote.isRenewal && quote.allowed && <Notice>This is a renewal. It starts on {date(quote.periodStart)}, the day after your current plan ends.</Notice>}
          {!quote.allowed && <Notice tone="warn">{quote.reasons.filter((r) => !/consent/i.test(r))[0] ?? "This plan cannot be bought right now."}</Notice>}
          <label className="check">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>I agree to pay the fee for this period in advance. Refunds follow the terms I signed.</span>
          </label>
          <button className="btn" onClick={() => void pay()} disabled={busy || !consent || !quote.allowed || !!order}>
            {busy ? "Starting payment…" : `Pay ${rupees(quote.totalPaise)}`}
          </button>
        </>
      )}

      {order && (
        <SandboxPanel title="Razorpay payment">
          <p>
            Order {order.orderId} for <strong className="num">{rupees(order.amountPaise)}</strong>. No money moves in test mode.
          </p>
          <button className="btn" onClick={() => void sandboxPay()} disabled={busy}>
            Complete test payment
          </button>
        </SandboxPanel>
      )}
    </div>
  );
}
